import {
  FILTER_MATCH_TYPE_SEPARATOR,
  FILTER_VALUE_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import { SearchMatchType } from '@aibees/elephant/types';
import { SearchBrowseParams } from '@aibees/shared/definition';
import { SearchFilterOption, SearchFilterOptionMetadata } from '../../types';

export function buildSearchParamsDraft(
  value: string,
  checked: boolean,
  currentValues: string[],
  options?: SearchFilterOption[],
  skipUrlParams?: boolean,
  optionMetadata?: SearchFilterOptionMetadata
) {
  const currentLength = currentValues.length;
  const optionsLength = options?.length ?? 0;

  // Handle skipUrlParams cases first
  if (skipUrlParams && !currentLength && !checked) {
    // Pre-allocate array size for better performance
    const draft = new Array(optionsLength - 1);
    let index = 0;
    for (let i = 0; i < optionsLength; i++) {
      const optionValue = options![i].value;
      if (optionValue !== value) {
        draft[index++] = optionValue;
      }
    }
    return draft.join(FILTER_VALUE_SEPARATOR);
  } else if (
    skipUrlParams &&
    currentLength &&
    currentLength === optionsLength - 1 &&
    checked
  ) {
    return undefined;
  } else if (!optionMetadata?.selectAll) {
    // Handle regular (non-selectAll) cases
    if (!checked) {
      // Removing value - use indexOf for faster lookup
      const valueIndex = currentValues.indexOf(value);
      if (valueIndex === -1) {
        // Value not found, return current state
        return currentLength
          ? currentValues.join(FILTER_VALUE_SEPARATOR)
          : undefined;
      }

      // Create new array without the value
      const draft = new Array(currentLength - 1);
      let newIndex = 0;
      for (let i = 0; i < currentLength; i++) {
        if (i !== valueIndex) {
          draft[newIndex++] = currentValues[i];
        }
      }
      return draft.length ? draft.join(FILTER_VALUE_SEPARATOR) : undefined;
    } else {
      // Adding value - need to remove selectAll if exists and add new value
      let selectAllValue: string | undefined;

      // Find selectAll value more efficiently
      if (options) {
        for (let i = 0; i < optionsLength; i++) {
          if (options[i].metadata?.selectAll) {
            selectAllValue = options[i].value;
            break;
          }
        }
      }

      // Build new array efficiently
      const draft = new Array(currentLength + 1); // Pre-allocate max possible size
      let newIndex = 0;

      // Copy existing values (excluding selectAll)
      for (let i = 0; i < currentLength; i++) {
        const currentValue = currentValues[i];
        if (currentValue !== selectAllValue) {
          draft[newIndex++] = currentValue;
        }
      }

      // Add new value
      draft[newIndex] = value;

      // Trim array to actual size and join
      draft.length = newIndex + 1;
      return draft.join(FILTER_VALUE_SEPARATOR);
    }
  } else if (optionMetadata.selectAll) {
    if (checked) {
      return value; // This becomes [value].join() = value
    } else {
      return undefined; // This becomes [].join() = undefined after length check
    }
  }

  return undefined;
}

export function isCheckboxDisabled(
  isFilterDisabled: boolean,
  values: number,
  checked?: boolean,
  required?: boolean
) {
  return isFilterDisabled || (!!checked && !!required && values === 1);
}

export function isCheckboxChecked(
  isFilterDisabled: boolean,
  values: string[],
  value?: string,
  defaultSelected?: boolean
) {
  return values.length
    ? values.includes(value!)
    : !isFilterDisabled && !!defaultSelected;
}

export function isComingSoon(value: string, comingSoonKeys?: string[]) {
  return !!comingSoonKeys?.includes(value);
}

export function buildKeywordsSearchMatchDraft(
  field: string,
  params: Partial<SearchBrowseParams>,
  searchType: SearchMatchType,
  paramsKey: string
) {
  const paramValue = params[paramsKey];
  if (!paramValue) {
    // Early exit: no existing params, just return new value
    return field + FILTER_MATCH_TYPE_SEPARATOR + searchType;
  }

  const draft = paramValue.split(FILTER_VALUE_SEPARATOR);
  const fieldPrefix = field + FILTER_MATCH_TYPE_SEPARATOR;
  const newFieldValue = fieldPrefix + searchType;

  // Find existing field index instead of using find + includes + filter
  let fieldIndex = -1;
  for (let i = 0; i < draft.length; i++) {
    if (draft[i].split(FILTER_MATCH_TYPE_SEPARATOR)[0] === field) {
      fieldIndex = i;
      break;
    }
  }

  if (fieldIndex !== -1) {
    // Found existing field
    const existingFieldValue = draft[fieldIndex];
    if (!existingFieldValue.includes(searchType)) {
      // Replace existing field with new searchType
      draft[fieldIndex] = newFieldValue;
    }
    // If it already includes searchType, no change needed
  } else {
    // No existing field found, add new one
    draft.push(newFieldValue);
  }

  return draft.join(FILTER_VALUE_SEPARATOR);
}

export function generateSearchMatchParamsKey(field: string) {
  return `${field}_search_match`;
}
